import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardBody, CardHeader, CardTitle, Input, cn } from '@ludus/ui'
import type { CardPage } from '../../types.ts'
import type { Interval } from '../CardView.tsx'

interface TranscriptPanelProps {
  transcript: CardPage['transcript']
  selection: Interval | null
  onSeek: (ms: number) => void
}

export function timecode(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/** The transcript with search and speaker roles; a timecode click seeks the audio. */
export function TranscriptPanel({ transcript, selection, onSeek }: TranscriptPanelProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  if (transcript === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {t('pages.audits.card.transcript.title', { defaultValue: 'Transcript' })}
          </CardTitle>
        </CardHeader>
        <CardBody className="text-xs text-muted">
          {t('pages.audits.card.transcript.none', { defaultValue: 'No transcript for this call.' })}
        </CardBody>
      </Card>
    )
  }
  const needle = query.trim().toLowerCase()
  const overlaps = (s: { startMs: number; endMs: number }) =>
    selection !== null && s.startMs < selection.endMs && s.endMs > selection.startMs
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('pages.audits.card.transcript.title', { defaultValue: 'Transcript' })}
          <span className="ml-2 text-xs font-normal text-muted">
            {transcript.language}
            {transcript.durationMs !== null ? ` · ${timecode(transcript.durationMs)}` : ''}
          </span>
        </CardTitle>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('pages.audits.card.transcript.search', {
            defaultValue: 'Search the transcript…',
          })}
          aria-label={t('pages.audits.card.transcript.search', {
            defaultValue: 'Search the transcript…',
          })}
        />
      </CardHeader>
      <CardBody>
        <ol className="flex flex-col gap-1 text-sm">
          {transcript.segments
            .filter((s) => needle === '' || s.text.toLowerCase().includes(needle))
            .map((segment) => (
              <li
                key={segment.idx}
                className={cn(
                  'grid grid-cols-[4rem_5.5rem_1fr] gap-2 rounded px-1 py-0.5',
                  overlaps(segment) && 'bg-primary-light',
                )}
              >
                <button
                  type="button"
                  className="text-left font-mono text-xs text-primary hover:underline"
                  onClick={() => onSeek(segment.startMs)}
                >
                  {timecode(segment.startMs)}
                </button>
                <span className="text-xs text-muted">
                  {segment.role
                    ? t(`pages.audits.card.transcript.role.${segment.role}`, {
                        defaultValue: segment.role,
                      })
                    : (segment.speaker ?? '—')}
                </span>
                <span>{segment.text}</span>
              </li>
            ))}
        </ol>
      </CardBody>
    </Card>
  )
}
